(function (module) {
  'use strict';

  /**
   * WebSocket Merge Strategy for rocket-ui
   * Handles VERSION_EXISTS conflicts to prevent data loss on concurrent updates
   *
   * This is a browser-compatible version of @rocket/shared/ws-merge
   */

  /**
   * Field ownership map - defines which service "owns" which fields
   */
  var FIELD_OWNERSHIP = {
    // PLAYER AUTHORITY
    cpu: 'player',
    ram: 'player',
    disk: 'player',
    temperature: 'player',
    uptime: 'player',
    rocket: 'player',
    networkInfo: 'player',
    resolution: 'player',
    network: 'player',
    networkType: 'player',
    'screenshot.large.image': 'player',
    'screenshot.small.image': 'player',

    // UI AUTHORITY
    'screenshot.large.enabled': 'ui',
    'screenshot.small.enabled': 'ui',

    // API/DATABASE AUTHORITY
    name: 'api',
    subName: 'api',
    brightness: 'api',
    brightnessControl: 'api',
    brightnessSchedule: 'api',
    volume: 'api',
    volumeControl: 'api',
    volumeSchedule: 'api',
    orientation: 'api',
    desiredResolution: 'api',
    position: 'api',
    bounds: 'api',
    fullscreen: 'api',
    on: 'api',
    timer: 'api',
    timeZone: 'api',
    extra: 'api',
    remote: 'api',
    online: 'api',
    lastSeen: 'api',
    verified: 'api',
    pairingCode: 'api',
    enabled: 'api',
  };

  function detectUpdateSource(data) {
    var keys = Object.keys(data);
    var playerFields = [
      'cpu', 'ram', 'disk', 'temperature', 'uptime',
      'rocket', 'networkInfo', 'resolution', 'network', 'networkType',
    ];

    if (keys.some(function (k) { return playerFields.indexOf(k) !== -1; })) return 'player';
    if (keys.some(function (k) { return k.indexOf('.image') !== -1; })) return 'player';
    if (keys.some(function (k) { return k.indexOf('.enabled') !== -1; })) return 'ui';

    var apiFields = [
      'name', 'subName', 'brightness', 'brightnessControl', 'brightnessSchedule',
      'volume', 'volumeControl', 'volumeSchedule', 'orientation', 'desiredResolution',
      'position', 'bounds', 'fullscreen', 'on', 'timer', 'timeZone', 'extra', 'remote',
      'online', 'lastSeen', 'verified', 'pairingCode', 'enabled',
    ];
    if (keys.some(function (k) { return apiFields.indexOf(k) !== -1; })) return 'api';

    return null;
  }

  function getFieldOwner(fieldPath) {
    if (FIELD_OWNERSHIP[fieldPath]) return FIELD_OWNERSHIP[fieldPath];
    var parts = fieldPath.split('.');
    for (var i = parts.length - 1; i > 0; i--) {
      var parentPath = parts.slice(0, i).join('.');
      if (FIELD_OWNERSHIP[parentPath]) return FIELD_OWNERSHIP[parentPath];
    }
    return null;
  }

  function getUpdateSource(data) {
    if (data._source) return data._source;
    return detectUpdateSource(data);
  }

  function getAllPaths(obj, prefix) {
    prefix = prefix || '';
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return [];

    var paths = [];
    for (var key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        var fullPath = prefix ? prefix + '.' + key : key;
        paths.push(fullPath);
        var value = obj[key];
        if (value && typeof value === 'object' && !Array.isArray(value) &&
            key !== '_source' && key !== '_timestamp') {
          paths = paths.concat(getAllPaths(value, fullPath));
        }
      }
    }
    return paths;
  }

  function deepMerge(target, source) {
    if (!target || typeof target !== 'object') return source;
    if (!source || typeof source !== 'object') return target;

    var result = {};
    // Copy target properties
    for (var key in target) {
      if (Object.prototype.hasOwnProperty.call(target, key)) {
        result[key] = target[key];
      }
    }

    // Merge source properties
    for (var skey in source) {
      if (Object.prototype.hasOwnProperty.call(source, skey)) {
        var sourceValue = source[skey];
        var targetValue = target[skey];

        if (sourceValue === null) {
          result[skey] = null;
        } else if (Array.isArray(sourceValue)) {
          result[skey] = sourceValue;
        } else if (sourceValue && typeof sourceValue === 'object' && !Array.isArray(sourceValue)) {
          var targetObj = targetValue && typeof targetValue === 'object' && !Array.isArray(targetValue)
            ? targetValue : {};
          result[skey] = deepMerge(targetObj, sourceValue);
        } else {
          result[skey] = sourceValue;
        }
      }
    }
    return result;
  }

  function mergeDisplayRecord(local, remote) {
    var _ = window._;
    var localSource = getUpdateSource(local);
    var remoteSource = getUpdateSource(remote);

    // Same-source conflicts: use timestamp (last-write-wins)
    if (localSource && remoteSource && localSource === remoteSource) {
      var localTime = local._timestamp || 0;
      var remoteTime = remote._timestamp || 0;
      return remoteTime >= localTime ? deepMerge(local, remote) : deepMerge(remote, local);
    }

    // Metadata-based preference
    if (!localSource && remoteSource && remote._timestamp) return deepMerge(local, remote);
    if (localSource && !remoteSource && local._timestamp) return deepMerge(remote, local);

    var merged = deepMerge(local, remote);

    // Apply field ownership rules
    if (localSource && remoteSource && localSource !== remoteSource) {
      var localPaths = getAllPaths(local);
      var remotePaths = getAllPaths(remote);
      var allPathsSet = {};
      localPaths.forEach(function (p) { allPathsSet[p] = true; });
      remotePaths.forEach(function (p) { allPathsSet[p] = true; });
      var allPaths = Object.keys(allPathsSet);

      allPaths.forEach(function (path) {
        var fieldOwner = getFieldOwner(path);
        if (fieldOwner) {
          var localValue = _.get(local, path);
          var remoteValue = _.get(remote, path);

          if (localSource === fieldOwner && remoteSource !== fieldOwner) {
            if (localValue !== undefined) _.set(merged, path, localValue);
          } else if (remoteSource === fieldOwner && localSource !== fieldOwner) {
            if (remoteValue !== undefined) _.set(merged, path, remoteValue);
          } else if (localSource !== fieldOwner && remoteSource !== fieldOwner) {
            if (localValue !== undefined) _.set(merged, path, localValue);
          }
        }
      });

      // Update timestamp to max
      var localTs = local._timestamp || 0;
      var remoteTs = remote._timestamp || 0;
      merged._timestamp = Math.max(localTs, remoteTs);
      merged._source = remoteTs > localTs ? remoteSource : localSource;
    }

    return merged;
  }

  function mergeContentRecord(local, remote) {
    return deepMerge(local, remote);
  }

  /**
   * Creates the merge strategy function for Deepstream client
   * @returns {Function} Merge strategy function
   */
  function createMergeStrategy() {
    return function customMergeStrategy(record, remoteData, remoteVersion, callback) {
      var recordName = record.name;

      try {
        var localData = record.get();
        var merged;

        if (recordName.indexOf('display:') === 0) {
          merged = mergeDisplayRecord(localData, remoteData);
        } else if (recordName.indexOf('playlist:') === 0 ||
                   recordName.indexOf('schedule:') === 0 ||
                   recordName.indexOf('app:') === 0) {
          merged = mergeContentRecord(localData, remoteData);
        } else {
          merged = deepMerge(localData, remoteData);
        }

        console.log('WS_MERGE: ' + recordName + ' merged (local v' + record.version + ' + remote v' + remoteVersion + ')');
        callback(null, merged);
      } catch (error) {
        console.error('WS_MERGE: error merging ' + recordName, error);
        callback(null, remoteData);
      }
    };
  }

  // Export to Angular module
  module.constant('wsMergeStrategy', {
    createMergeStrategy: createMergeStrategy,
    deepMerge: deepMerge,
    mergeDisplayRecord: mergeDisplayRecord,
    mergeContentRecord: mergeContentRecord,
    FIELD_OWNERSHIP: FIELD_OWNERSHIP,
  });

})(angular.module('rocket.components'));
